import React from "react";
import { money } from "@/lib/financeApi";
import { Chip, INVOICE_CHIP } from "@/components/nds/windows/finance/parts";

const H = ({ n, t, sub }) => (
  <div className="flex flex-wrap items-baseline gap-2"><span className="font-mono text-[10px] text-nex-gold">{n}</span><span className="font-serif text-nex-silver">{t}</span>{sub && <span className="text-[11px] text-nex-muted">{sub}</span>}</div>
);

export function Section({ n, t, sub, children, testid }) {
  return <section className="space-y-2 p-4 rounded-md border border-white/10 bg-white/[0.02]" data-testid={testid}><H n={n} t={t} sub={sub} />{children}</section>;
}

function InvoiceRow({ i }) {
  const [tone, label] = INVOICE_CHIP[i.status] || ["grey", i.status];
  return (
    <div className="py-1.5 border-t border-white/5 text-[11px]" data-testid={`lp-invoice-${i.number}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-nex-silver">#{i.number}</span><Chip tone={tone}>{label}</Chip>
        <span className="text-nex-muted">{i.period}</span>
        <span className={`font-mono ${i.days_open > 30 ? "text-red-300" : i.days_open > 14 ? "text-amber-200" : "text-nex-muted"}`} data-testid={`lp-invoice-days-${i.number}`}>{i.days_open ?? "—"} days open</span>
        <span className="ml-auto font-mono text-nex-silver">{money(i.awaiting)}</span>
      </div>
      <div className="pl-3 text-nex-muted">{i.residents.map((r) => <span key={`${r.resident_id}-${r.paycheck_id}`} className="mr-3">{r.resident} {money(r.amount)}</span>)}</div>
    </div>
  );
}

export function Awaiting({ a }) {
  return (
    <Section n="1" t="Invoices awaiting funding" sub={`${a.count} open · ${money(a.total)} we're waiting to receive`} testid="lp-awaiting">
      {!a.employers.length && <div className="text-xs text-nex-muted">No open employer invoices.</div>}
      {a.employers.map((e) => (
        <div key={e.employer} className="space-y-0.5" data-testid={`lp-employer-${e.employer}`}>
          <div className="flex items-center text-xs"><span className="text-nex-silver">{e.employer}</span><span className="ml-auto font-mono text-amber-200">{money(e.awaiting)}</span></div>
          {e.invoices.map((i) => <InvoiceRow key={i.id} i={i} />)}
        </div>))}
    </Section>
  );
}

export function Payables({ a, p, landlord }) {
  return (
    <Section n="2" t="Landlord payables" sub={`rent share of paystubs already issued (expected rent where no paystub yet) · ${landlord}`} testid="lp-payables">
      {a.employers.flatMap((e) => e.invoices).map((i) => (
        <div key={i.id} className="flex flex-wrap items-center gap-2 text-[11px] border-t border-white/5 py-1" data-testid={`lp-payable-${i.number}`}>
          <span className="font-mono text-nex-silver">#{i.number}</span><span className="text-nex-muted">{i.employer}</span>
          {i.no_paystub > 0 && <span className="text-amber-200/80">{i.no_paystub} line(s) with no paystub yet · expected rent counted</span>}
          <span className="ml-auto font-mono">{money(i.rent)}</span>
        </div>))}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 text-xs">
        <div data-testid="lp-gross-open"><div className="nds-label-dim text-[9px]">Owed once open invoices fund</div><div className="font-mono text-nex-silver">{money(p.gross_open)}</div></div>
        <div data-testid="lp-gross-funded"><div className="nds-label-dim text-[9px]">Rent from invoices already funded</div><div className="font-mono text-nex-silver">{money(p.gross_funded)}</div></div>
        <div data-testid="lp-gross-total"><div className="nds-label-dim text-[9px]">Gross landlord payable</div><div className="font-mono text-nex-gold">{money(p.gross_total)}</div></div>
      </div>
    </Section>
  );
}
